import React, { useRef, useLayoutEffect } from 'react';
import { TabWrapper, Indicator, TabsRoot } from './Tabs.styles';
import { ToggleItem, ToggleItemProps } from './components/ToggleItem/ToggleItem';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useTimeout } from '@shared/hooks/useTimeout';
import { useSpring } from 'react-spring';
import { TextV2Props } from '@withjoy/joykit';

export type TabItemProps = Pick<ToggleItemProps, 'label' | 'value' | 'isActive' | 'onClick' | 'isDisabled'>;

interface Props
  extends Readonly<{
    items: ReadonlyArray<TabItemProps>;
    isMobile?: boolean;
    width?: string;
    tabTextProps?: TextV2Props;
  }> {}

const Tabs: React.FC<Props> = ({ items, isMobile, width: TabsRootWidth, tabTextProps }) => {
  const isMounted = useIsMounted();
  const timeout = useTimeout();
  const tabsContainer = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);

  const [{ left, width: tabWidth }, set] = useSpring(() => ({ width: 0, left: 0 }));

  useLayoutEffect(() => {
    if (isMounted) {
      if (indicatorRef.current) {
        if (tabsContainer.current) {
          const children = Array.from(tabsContainer.current.children);
          const activeTab = (children as HTMLElement[]).find(child => child.classList.contains('active'));
          if (activeTab) {
            // Delay measurement to allow flex layout and translations to complete before calculating indicator width
            timeout.set(() => {
              const { width } = activeTab.getBoundingClientRect();
              set({ left: activeTab.offsetLeft, width });
            }, 50);
          }
        }
      }
    }
  }, [isMounted, items, isMobile, tabTextProps, TabsRootWidth, set, timeout]);

  return (
    <TabsRoot width={TabsRootWidth}>
      <TabWrapper ref={tabsContainer}>
        {items.map(item => (
          <ToggleItem
            key={item.label}
            isMobile={isMobile}
            isDisabled={item.isDisabled}
            textProps={tabTextProps}
            {...item}
            onClick={e => {
              if (!item.isActive) {
                item.onClick?.(e);
              }
            }}
          />
        ))}
      </TabWrapper>
      <Indicator
        ref={indicatorRef}
        style={{ transform: left.interpolate(value => `translate3d(${value}px, -50%, 0px)`), willChange: 'transform', width: tabWidth }}
      />
    </TabsRoot>
  );
};

export { Tabs };
